import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useApp } from "../context/AppContext";
import Logo from "../components/Logo";
import { LOGO_SIDEBAR_MARK_EXPANDED, getPublicAssetUrl } from "../config/logoSizes";
const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser, language, toggleLanguage, checkAuth } = useApp();
  const isAr = language === "ar";
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [delMsg, setDelMsg] = useState(null);
  const [delReason, setDelReason] = useState("");
  const isDeletionPending = user?.deletionRequest?.status === "pending";
  const submitDeleteRequest = async () => {
    setDelLoading(true);
    setDelMsg(null);
    try {
      const res = await axios.post("/api/auth/delete-account-request", { reason: delReason });
      setUser((prev) => prev ? { ...prev, deletionRequest: res.data.deletionRequest } : prev);
      setDelMsg({ type: "success", text: t("deleteAccountRequested") });
      setShowDeleteModal(false);
      setDelReason("");
      if (typeof checkAuth === "function") checkAuth();
    } catch (err) {
      setDelMsg({ type: "error", text: err.response?.data?.message || t("deleteAccountFailed") });
    } finally {
      setDelLoading(false);
    }
  };
  const cancelDeleteRequest = async () => {
    setDelLoading(true);
    setDelMsg(null);
    try {
      const res = await axios.post("/api/auth/delete-account-cancel");
      setUser((prev) => prev ? { ...prev, deletionRequest: res.data.deletionRequest } : prev);
      setDelMsg({ type: "success", text: t("deleteAccountCancelled") });
    } catch (err) {
      setDelMsg({ type: "error", text: err.response?.data?.message || t("deleteAccountFailed") });
    } finally {
      setDelLoading(false);
    }
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [fontSize, setFontSize] = useState(() => {
    const stored = localStorage.getItem("taweelai_fontsize");
    return stored ? parseInt(stored, 10) : 14;
  });
  const [fontSizeInput, setFontSizeInput] = useState(() => {
    const stored = localStorage.getItem("taweelai_fontsize");
    return stored || "14";
  });
  const applyFontSize = (size) => {
    const clamped = Math.max(12, Math.min(20, size));
    setFontSize(clamped);
    setFontSizeInput(String(clamped));
    localStorage.setItem("taweelai_fontsize", String(clamped));
    document.documentElement.style.setProperty("--site-font-size", `${clamped}px`);
  };
  React.useEffect(() => {
    const stored = localStorage.getItem("taweelai_fontsize");
    if (stored) document.documentElement.style.setProperty("--site-font-size", `${stored}px`);
  }, []);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const memberSince = user?.createdAt ? new Date(user.createdAt).toLocaleDateString(isAr ? "ar-EG" : "en-US", { year: "numeric", month: "long" }) : "\u2014";
  const isPaidHourly = user?.plan?.type === "hourly" && user?.plan?.isActive && user?.plan?.endTime &&  new Date() < new Date(user.plan.endTime);
  const isPaidMonthly = user?.plan?.type === "monthly" && user?.plan?.monthlyExpiry &&  new Date() < new Date(user.plan.monthlyExpiry);
  const isPaid = isPaidHourly || isPaidMonthly;
  const provider = user?.authProvider ?? "email";
  const oauthOnly = provider === "google" || provider === "facebook" || provider === "apple";
  const canChangePassword =
    !oauthOnly &&
    (user?.hasPassword === true ||
      (user?.hasPassword == null && (provider === "email" || user?.authProvider == null)));
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwNew !== pwConfirm) {
      setPwMsg({ type: "error", text: t("passwordsMatch") });
      return;
    }
    setPwLoading(true);
    try {
      await axios.patch("/api/auth/change-password", {
        currentPassword: pwCurrent,
        newPassword: pwNew
      });
      setPwMsg({ type: "success", text: t("passwordChanged") });
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
    } catch (err) {
      setPwMsg({ type: "error", text: err.response?.data?.message || t("passwordChangeFailed") });
    } finally {
      setPwLoading(false);
    }
  };
  const getPlanStatusText = () => {
    if (isPaidMonthly) {
      const exp = new Date(user.plan.monthlyExpiry);
      const daysLeft = Math.max(0, Math.ceil((exp -  new Date()) / (1e3 * 60 * 60 * 24)));
      return isAr ? `\u0628\u0627\u0642\u0629 Pro \u2014 ${daysLeft} \u064A\u0648\u0645 \u0645\u062A\u0628\u0642\u064A` : `Pro Plan \u2014 ${daysLeft} days remaining`;
    }
    if (isPaidHourly) return t("hourlyPlanStatus");
    if (user?.plan?.type === "hourly") return t("noSessionStatus");
    return isAr ? `\u0645\u062C\u0627\u0646\u064A\u0629 \u2014 ${Math.max(0, 10 - (user?.messageCount || 0))}/10 \u0631\u0633\u0627\u0644\u0629 \u0645\u062A\u0628\u0642\u064A\u0629` : `Free \u2014 ${Math.max(0, 10 - (user?.messageCount || 0))}/10 messages remaining`;
  };
  const getPlanBadge = () => {
    if (isPaidMonthly) return { label: "\u{1F451} Pro", color: "var(--accent)" };
    if (isPaidHourly) return { label: "\u26A1 Active", color: "var(--accent)" };
    return { label: "\u{1F193} Free", color: "var(--text-3)" };
  };
  const badge = getPlanBadge();
  return <div
    className="app-layout"
    onClick={() => navigate("/chat")}
    title={isAr ? "\u0627\u0646\u0642\u0631 \u062E\u0627\u0631\u062C \u0627\u0644\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0644\u0644\u0639\u0648\u062F\u0629 \u0644\u0644\u0645\u062D\u0627\u062F\u062B\u0629" : "Click outside to go back to chat"}
    style={{ cursor: "default" }}
  >
      {sidebarOpen && <div
    onClick={(e) => {
      e.stopPropagation();
      setSidebarOpen(false);
    }}
    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 199 }}
  />}
      <aside
    className={`sidebar ${sidebarOpen ? "open" : ""} ${sidebarCollapsed ? "collapsed" : ""}`}
    style={{ display: "flex", width: sidebarCollapsed ? 64 : void 0, transition: "width .2s ease" }}
    onClick={(e) => e.stopPropagation()}
  >
        <div
    className="sidebar-logo"
    onClick={() => navigate("/chat")}
    style={{
      cursor: "pointer",
      ...(sidebarCollapsed ? { position: "relative", padding: "0 10px 2px", justifyContent: "center" } : {})
    }}
  >
          {sidebarCollapsed ? <img
    src={getPublicAssetUrl("collapsed-icon.svg")}
    alt=""
    className="logo-img sidebar-collapsed-icon-img"
    draggable={false}
  /> : <Logo
    variant="mark"
    size={LOGO_SIDEBAR_MARK_EXPANDED}
    className="logo-img"
    style={{ borderRadius: 0, background: "transparent" }}
  />}
          <button
    className="sidebar-collapse-icon"
    onClick={(e) => {
      e.stopPropagation();
      setSidebarCollapsed((c) => !c);
    }}
    title={sidebarCollapsed ? isAr ? "\u0627\u0641\u062A\u062D \u0627\u0644\u0634\u0631\u064A\u0637 \u0627\u0644\u062C\u0627\u0646\u0628\u064A" : "Open sidebar" : isAr ? "\u0627\u063A\u0644\u0642 \u0627\u0644\u0634\u0631\u064A\u0637 \u0627\u0644\u062C\u0627\u0646\u0628\u064A" : "Close sidebar"}
    aria-label={sidebarCollapsed ? isAr ? "\u0627\u0641\u062A\u062D \u0627\u0644\u0634\u0631\u064A\u0637 \u0627\u0644\u062C\u0627\u0646\u0628\u064A" : "Open sidebar" : isAr ? "\u0627\u063A\u0644\u0642 \u0627\u0644\u0634\u0631\u064A\u0637 \u0627\u0644\u062C\u0627\u0646\u0628\u064A" : "Close sidebar"}
  >
            {sidebarCollapsed ? "\u25B6" : "\u25C0"}
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <div className="sidebar-footer" style={sidebarCollapsed ? { padding: "5px 4px" } : void 0}>
          <button
    className="sf-btn"
    onClick={() => navigate("/chat")}
    style={sidebarCollapsed ? { justifyContent: "center", padding: "9px 0" } : void 0}
  >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            {!sidebarCollapsed && (isAr ? "\u0627\u0644\u0639\u0648\u062F\u0629 \u0644\u0644\u0645\u062D\u0627\u062F\u062B\u0629" : "Back to Chat")}
          </button>
        </div>
      </aside>
      <div className="main-content" onClick={() => navigate("/chat")}>
        <div className="topbar" onClick={(e) => e.stopPropagation()}>
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen((v) => !v)}>☰</button>
          <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)", fontFamily: "Outfit,sans-serif" }}>
            ⚙️ {t("settingsTitle")}
          </span>
        </div>
        <div className="settings-page" onClick={(e) => e.stopPropagation()}>
          <div className="settings-section">
            <div className="settings-section-title">{t("appearance")}</div>
            <div className="settings-row">
              <div className="sr-label">
                <strong>{t("languageLabel")}</strong>
                <span>{t("languageDesc")}</span>
              </div>
              <select
    className="settings-select"
    value={language}
    onChange={(e) => toggleLanguage(e.target.value)}
  >
                <option value="en">🇺🇸 English</option>
                <option value="ar">🇸🇦 العربية</option>
              </select>
            </div>
            {}
            <div className="settings-row">
              <div className="sr-label">
                <strong>{isAr ? "\u062D\u062C\u0645 \u0627\u0644\u062E\u0637" : "Font Size"}</strong>
                <span>{isAr ? "\u062D\u062C\u0645 \u0646\u0635 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0627\u062A" : "Chat message text size"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
    onClick={() => applyFontSize(fontSize - 1)}
    style={{
      width: 30,
      height: 30,
      borderRadius: 8,
      background: "var(--bg-hover)",
      border: "1px solid var(--border2)",
      color: "var(--text-1)",
      fontSize: 16,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}
  >−</button>
                <input
    type="number"
    value={fontSizeInput}
    min={12}
    max={20}
    onChange={(e) => {
      setFontSizeInput(e.target.value);
      const n = parseInt(e.target.value, 10);
      if (!isNaN(n) && n >= 12 && n <= 20) applyFontSize(n);
    }}
    onBlur={() => {
      const n = parseInt(fontSizeInput, 10);
      applyFontSize(isNaN(n) ? 14 : n);
    }}
    style={{
      width: 52,
      textAlign: "center",
      padding: "5px",
      fontSize: 14,
      fontWeight: 700,
      fontFamily: "inherit",
      background: "var(--bg-input)",
      border: "1px solid var(--border2)",
      borderRadius: 8,
      color: "var(--text-1)",
      outline: "none"
    }}
  />
                <span style={{ fontSize: 11, color: "var(--text-3)" }}>px</span>
                <button
    onClick={() => applyFontSize(fontSize + 1)}
    style={{
      width: 30,
      height: 30,
      borderRadius: 8,
      background: "var(--bg-hover)",
      border: "1px solid var(--border2)",
      color: "var(--text-1)",
      fontSize: 16,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}
  >+</button>
                {}
                <button
    onClick={() => applyFontSize(14)}
    title={isAr ? "\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u0636\u0628\u0637" : "Reset to default"}
    style={{
      height: 30,
      padding: "0 10px",
      borderRadius: 8,
      background: fontSize !== 14 ? "var(--accent-glow)" : "var(--bg-hover)",
      border: `1px solid ${fontSize !== 14 ? "var(--accent-b2)" : "var(--border2)"}`,
      color: fontSize !== 14 ? "var(--accent)" : "var(--text-3)",
      fontSize: 11,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "inherit",
      whiteSpace: "nowrap",
      transition: "all .15s"
    }}
  >{isAr ? "\u0627\u0641\u062A\u0631\u0627\u0636\u064A" : "Reset"}</button>
              </div>
            </div>
          </div>
          {}
          <div className="settings-section">
            <div className="settings-section-title">{t("account")}</div>
            <div className="settings-row">
              <div className="sr-label">
                <strong>{t("accountInfo")}</strong>
                <span>{user?.email || "\u2014"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {user?.avatar ? <img src={user.avatar} alt="" style={{ width: 36, height: 36, borderRadius: "50%", border: "2px solid var(--border2)" }} /> : <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14 }}>
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </div>}
              </div>
            </div>
            <div className="settings-row">
              <div className="sr-label">
                <strong>{t("memberSince")}</strong>
                <span>{memberSince}</span>
              </div>
            </div>
            <div className="settings-row">
              <div className="sr-label">
                <strong>{t("planStatus")}</strong>
                <span>{getPlanStatusText()}</span>
              </div>
              <div style={{
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: "var(--bg-base)",
    border: `1px solid ${isPaid ? "var(--border3)" : "var(--border2)"}`,
    color: isPaid ? "var(--accent)" : "var(--text-3)"
  }}>
                {badge.label}
              </div>
            </div>
          </div>
          {canChangePassword && <div className="settings-section">
              <div className="settings-section-title">{t("changePassword")}</div>
              {pwMsg && <div className={`form-alert ${pwMsg.type}`} style={{ marginBottom: 12 }}>
                  {pwMsg.text}
                </div>}
              <form onSubmit={handleChangePassword}>
                {}
                {(() => {
    const EyeIcon = ({ show }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {show ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></> : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>}
                    </svg>;
    const pwFieldStyle = { position: "relative" };
    const eyeBtnStyle = {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      right: 12,
      background: "none",
      border: "none",
      color: "var(--text-3)",
      cursor: "pointer",
      padding: 2,
      display: "flex",
      alignItems: "center"
    };
    return <>
                      <div className="form-group">
                        <label className="form-label">{t("currentPasswordLabel")}</label>
                        <div style={pwFieldStyle}>
                          <input
      className="form-input"
      type={showPwCurrent ? "text" : "password"}
      autoComplete="current-password"
      value={pwCurrent}
      onChange={(e) => setPwCurrent(e.target.value)}
      style={{ paddingRight: 40 }}
      required
    />
                          <button type="button" style={eyeBtnStyle} onClick={() => setShowPwCurrent((v) => !v)}>
                            <EyeIcon show={showPwCurrent} />
                          </button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t("newPasswordLabel")}</label>
                        <div style={pwFieldStyle}>
                          <input
      className="form-input"
      type={showPwNew ? "text" : "password"}
      autoComplete="new-password"
      value={pwNew}
      onChange={(e) => setPwNew(e.target.value)}
      style={{ paddingRight: 40 }}
      required
    />
                          <button type="button" style={eyeBtnStyle} onClick={() => setShowPwNew((v) => !v)}>
                            <EyeIcon show={showPwNew} />
                          </button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t("confirmNewPasswordLabel")}</label>
                        <div style={pwFieldStyle}>
                          <input
      className="form-input"
      type={showPwConfirm ? "text" : "password"}
      autoComplete="new-password"
      value={pwConfirm}
      onChange={(e) => setPwConfirm(e.target.value)}
      style={{ paddingRight: 40 }}
      required
    />
                          <button type="button" style={eyeBtnStyle} onClick={() => setShowPwConfirm((v) => !v)}>
                            <EyeIcon show={showPwConfirm} />
                          </button>
                        </div>
                      </div>
                    </>;
  })()}
                <button className="btn-primary" type="submit" disabled={pwLoading}>
                  {pwLoading ? "\u2026" : t("updatePassword")}
                </button>
              </form>
            </div>}
          {user && <div className="settings-section">
              <div className="settings-section-title">{isAr ? "\u0637\u0631\u064A\u0642\u0629 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644" : "Sign-in Method"}</div>
              <div className="settings-row">
                <div className="sr-label">
                  <strong>{isAr ? "\u0627\u0644\u0645\u0632\u0648\u062F" : "Provider"}</strong>
                  <span style={{ textTransform: "capitalize" }}>{provider}</span>
                </div>
                <span style={{ fontSize: 20 }}>
                  {provider === "google" ? "\u{1F535}" : provider === "facebook" ? "\u{1F537}" : "\u{1F4E7}"}
                </span>
              </div>
            </div>}
          {user && user.role !== "admin" && <div className="settings-section">
              <div className="settings-section-title" style={{ color: "#dc2626" }}>{t("deleteAccountTitle")}</div>
              {delMsg && <div className={`form-alert ${delMsg.type}`} style={{ marginBottom: 12 }}>{delMsg.text}</div>}
              <div className="settings-row">
                <div className="sr-label">
                  <strong>{t("deleteAccount")}</strong>
                  <span>{isDeletionPending ? t("deleteAccountPending") : t("deleteAccountDesc")}</span>
                </div>
                {isDeletionPending ? <button
    type="button"
    onClick={cancelDeleteRequest}
    disabled={delLoading}
    style={{
      padding: "8px 14px",
      borderRadius: 10,
      border: "1px solid var(--border2)",
      background: "var(--bg-hover)",
      color: "var(--text-1)",
      fontWeight: 600,
      fontFamily: "inherit",
      fontSize: 13,
      cursor: delLoading ? "not-allowed" : "pointer",
      opacity: delLoading ? 0.6 : 1
    }}
  >{delLoading ? "\u2026" : t("deleteAccountCancel")}</button> : <button
    type="button"
    onClick={() => setShowDeleteModal(true)}
    style={{
      padding: "8px 14px",
      borderRadius: 10,
      border: "1px solid #dc2626",
      background: "#fef2f2",
      color: "#dc2626",
      fontWeight: 700,
      fontFamily: "inherit",
      fontSize: 13,
      cursor: "pointer"
    }}
  >{t("deleteAccount")}</button>}
              </div>
            </div>}
        </div>
      </div>
      {showDeleteModal && <div
    onClick={(e) => {
      e.stopPropagation();
      if (!delLoading) setShowDeleteModal(false);
    }}
    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
  >
        <div
    onClick={(e) => e.stopPropagation()}
    style={{ background: "var(--bg-card)", border: "1px solid var(--border2)", borderRadius: 16, maxWidth: 460, width: "100%", padding: 24, color: "var(--text-1)", direction: isAr ? "rtl" : "ltr" }}
  >
          <h3 style={{ margin: 0, fontSize: 18, color: "#dc2626" }}>{t("deleteAccountTitle")}</h3>
          <p style={{ marginTop: 10, fontSize: 14, color: "var(--text-2)", lineHeight: 1.55 }}>{t("deleteAccountConfirm")}</p>
          <textarea
    value={delReason}
    onChange={(e) => setDelReason(e.target.value)}
    placeholder={t("deleteAccountReasonPlaceholder")}
    rows={3}
    maxLength={500}
    style={{ width: "100%", marginTop: 12, padding: 10, borderRadius: 10, border: "1px solid var(--border2)", background: "var(--bg-input)", color: "var(--text-1)", fontFamily: "inherit", fontSize: 13, resize: "vertical" }}
  />
          <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
            <button
    type="button"
    onClick={() => setShowDeleteModal(false)}
    disabled={delLoading}
    style={{ padding: "9px 14px", borderRadius: 10, border: "1px solid var(--border2)", background: "transparent", color: "var(--text-2)", fontFamily: "inherit", fontSize: 13, cursor: "pointer" }}
  >{t("cancel")}</button>
            <button
    type="button"
    onClick={submitDeleteRequest}
    disabled={delLoading}
    style={{ padding: "9px 14px", borderRadius: 10, border: "1px solid #dc2626", background: "#dc2626", color: "#fff", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: delLoading ? "not-allowed" : "pointer", opacity: delLoading ? 0.7 : 1 }}
  >{delLoading ? "\u2026" : t("deleteAccountSubmit")}</button>
          </div>
        </div>
      </div>}
    </div>;
};
var stdin_default = Settings;
export {
  stdin_default as default
};
